import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message, Button, Empty, Input, Modal, Popconfirm, Tag, Tree } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from '../common/Tooltip';
import Loader from '../../Loader';
import {
  ICONS,
  ColumnFilterDropdown,
  SortIcon,
  buildColumnFilterOptions,
  compareTextValues,
  matchesColumnFilters,
  nextSortState,
  normalizeText,
} from './DocumentsFolderView';
import NewWebTemplateModal from './NewWebTemplateModal';
import { KIND_STANDALONE, isStandaloneTemplate, kindLabel } from '../../webviewer/utils/webTemplateKind';
import {
  fetchWebTemplates,
  createWebTemplate,
  deleteWebTemplate,
  duplicateWebTemplate,
  renameWebTemplate,
} from '../../webviewer/requests/webTemplates';
import './DocumentsFolderView.css';

// Onglet Site web : même tableau que la vue Documents (DocumentsFolderView),
// dont il réutilise l'en-tête, le tri, les filtres et les lignes (Tree stylé).
// Clic sur une ligne = ouvrir l'éditeur dans un nouvel onglet navigateur
// (/web-templates?edit=..., l'éditeur Puck n'est pas conçu pour être monté
// inline) ; les actions au survol sont celles d'un document : renommer,
// dupliquer, supprimer.
// Avec `onApply`, le tableau sert à choisir un modèle (création d'un site web
// seul, cf. ProposalList ; génération d'un site, cf. WebTemplatePicker), comme
// DocumentsFolderView `templatesOnly` : ni création ni actions, clic sur une
// ligne = l'appliquer.
// `onEdit` remplace l'ouverture d'onglet quand le tableau est déjà dans l'app
// /web-templates (cf. webTemplateAdminApp).

const GRID_COLUMNS = 'minmax(140px, 1fr) 110px 132px 110px 108px';
// Sélecteur : le document est connu, donc son type et son métier (une offre
// n'en a pas), et la liste est déjà filtrée sur l'usage (cf.
// fetchWebTemplatesForDocument) : reste le nom et la date.
const PICKER_GRID_COLUMNS = 'minmax(140px, 1fr) 110px';

const NO_FILTER = [];

const editWebTemplate = (id) => window.open(`/web-templates?edit=${id}`, '_blank');

// Le back renvoie la liste sans ORDER BY : Postgres la rend dans un ordre
// arbitraire (une ligne modifiée peut changer de place). Sans tri actif, la
// liste suit donc l'ordre de création, un duplicata arrivant en bas.
const byCreation = (a, b) => String(a.created_at ?? '').localeCompare(String(b.created_at ?? '')) || a.id - b.id;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fr-FR') : '—');

const WebTemplatesPanel = ({
  iconStyle = 'light', onApply, onEdit = editWebTemplate, fetchTemplates = fetchWebTemplates,
}) => {
  const picker = !!onApply;
  const gridColumns = picker ? PICKER_GRID_COLUMNS : GRID_COLUMNS;
  const { t } = useTranslation();
  const faIcon = (name) => ICONS[name][iconStyle];
  const [webTemplates, setWebTemplates] = useState([]);
  const [webTemplatesLoaded, setWebTemplatesLoaded] = useState(false);
  const [creatingWebTemplate, setCreatingWebTemplate] = useState(false);
  const [newWebTemplateModalOpen, setNewWebTemplateModalOpen] = useState(false);
  const [renamer, setRenamer] = useState(null); // { id, name }
  const [nameSearch, setNameSearch] = useState('');
  const [sortState, setSortState] = useState({ field: null, order: null });
  const [columnFilters, setColumnFilters] = useState({});
  const renameInputRef = useRef(null);

  const loadWebTemplates = async () => {
    try {
      setWebTemplates([...(await fetchTemplates())].sort(byCreation));
    } catch (e) {
      message.error(e.message);
    } finally {
      setWebTemplatesLoaded(true);
    }
  };

  useEffect(() => {
    loadWebTemplates();
  }, []);

  const runAndReload = async (action, successMessage) => {
    try {
      await action();
      if (successMessage) message.success(successMessage);
      await loadWebTemplates();
      return true;
    } catch (e) {
      message.error(e.message);
      return false;
    }
  };

  const handleCreate = async (values) => {
    setCreatingWebTemplate(true);
    try {
      const created = await createWebTemplate(values);
      setNewWebTemplateModalOpen(false);
      onEdit(created.id);
      await loadWebTemplates();
    } catch (e) {
      message.error(e.message);
    } finally {
      setCreatingWebTemplate(false);
    }
  };

  const submitRename = async () => {
    const name = renamer?.name?.trim();
    if (!name) return;
    const renamed = await runAndReload(() => renameWebTemplate(renamer.id, name));
    if (renamed) setRenamer(null);
  };

  const fieldValue = (template, field) => {
    switch (field) {
      case 'name': return template.name;
      case 'type': return t(template.templatable || '');
      case 'work': return t(template.work || '');
      case 'updated_at': return template.updated_at || '';
      default: return '';
    }
  };

  const filterOptions = useMemo(
    () => buildColumnFilterOptions({ items: webTemplates, translate: t }),
    [webTemplates, t],
  );

  const visibleWebTemplates = useMemo(() => {
    const needle = nameSearch.trim() ? normalizeText(nameSearch) : null;
    const filtered = webTemplates.filter((template) =>
      (!needle || normalizeText(template.name).includes(needle))
      && matchesColumnFilters(template, columnFilters));
    if (!sortState.field || !sortState.order) return filtered;
    const dir = sortState.order === 'ascend' ? 1 : -1;
    return [...filtered].sort((a, b) =>
      compareTextValues(fieldValue(a, sortState.field), fieldValue(b, sortState.field)) * dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webTemplates, nameSearch, columnFilters, sortState, t]);

  const treeData = visibleWebTemplates.map((template) => ({
    key: template.id,
    // title: '' — sans ça, rc-tree pose son defaultTitle ('---') en infobulle
    // native (cf. DocumentsFolderView).
    title: '',
    isLeaf: true,
    template,
  }));

  const sortableHeader = (field, label, filterable = false) => (
    <span className='doc-tree-header-sortable' onClick={() => setSortState((prev) => nextSortState(prev, field))}>
      <span className='doc-tree-header-title'>{label}</span>
      <SortIcon order={sortState.field === field ? sortState.order : null} />
      {filterable && (
        <ColumnFilterDropdown
          options={filterOptions[field]}
          value={columnFilters[field] ?? NO_FILTER}
          onChange={(next) => setColumnFilters((prev) => ({ ...prev, [field]: next }))}
        />
      )}
    </span>
  );

  const renderRow = ({ template }) => (
    <div className='doc-tree-row' style={{ gridTemplateColumns: gridColumns }}>
      <span className='doc-tree-cell' style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <FontAwesomeIcon icon={faIcon('globe')} style={{ opacity: 0.6 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{template.name}</span>
        {!picker && isStandaloneTemplate(template) && <Tag bordered={false} color='cyan'>{kindLabel(KIND_STANDALONE)}</Tag>}
      </span>
      {!picker && (
        <>
          <span className='doc-tree-cell'>{template.templatable ? t(template.templatable) : '—'}</span>
          <span className='doc-tree-cell'>{template.work ? t(template.work) : '—'}</span>
        </>
      )}
      <span className='doc-tree-cell'>{formatDate(template.updated_at)}</span>
      {!picker && (
        <span className='doc-tree-actions' onClick={(e) => e.stopPropagation()}>
          <Tooltip title='Renommer' mouseLeaveDelay={0}>
            <FontAwesomeIcon
              icon={faIcon('pen')}
              style={actionIconStyle}
              onClick={() => setRenamer({ id: template.id, name: template.name })}
            />
          </Tooltip>
          <Tooltip title='Dupliquer' mouseLeaveDelay={0}>
            <FontAwesomeIcon
              icon={faIcon('copy')}
              style={actionIconStyle}
              onClick={() => runAndReload(() => duplicateWebTemplate(template.id))}
            />
          </Tooltip>
          <Tooltip title='Supprimer' mouseLeaveDelay={0}>
            <Popconfirm
              title='Supprimer ce site web ?'
              okText={t('yes')}
              cancelText={t('cancel')}
              onConfirm={() => runAndReload(() => deleteWebTemplate(template.id), `Template "${template.name}" supprimé`)}
            >
              <FontAwesomeIcon icon={faIcon('trash')} style={{ ...actionIconStyle, color: '#cf1322' }} />
            </Popconfirm>
          </Tooltip>
        </span>
      )}
    </div>
  );

  const renderList = () => {
    if (!webTemplatesLoaded) {
      return (
        <Loader
          style={{ flex: 1, minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          imageStyle={{ width: 240, maxWidth: '50%' }}
        />
      );
    }
    if (treeData.length === 0) {
      const searching = nameSearch.trim() || Object.values(columnFilters).some((values) => values.length > 0);
      const emptyText = picker ? 'Aucun modèle de site web pour ce document' : 'Aucun site web';
      return <Empty description={searching ? 'Aucun site web ne correspond à la recherche' : emptyText} />;
    }
    return (
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Tree
          className='documents-folder-tree'
          treeData={treeData}
          titleRender={renderRow}
          blockNode
          selectable
          // Sélecteur : la ligne choisie reste surlignée, comme le classique.
          selectedKeys={picker ? undefined : []}
          onSelect={(_keys, { node }) => (
            picker ? onApply(node.template.id, node.template) : onEdit(node.template.id)
          )}
        />
      </div>
    );
  };

  return (
    <div style={{ textAlign: 'left', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!picker && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button
              type='primary'
              ghost
              icon={<FontAwesomeIcon icon={faIcon('file-plus')} style={{ marginRight: 8 }} />}
              onClick={() => setNewWebTemplateModalOpen(true)}
            >
              Nouveau site web
            </Button>
          </div>
        </div>
      )}

      {/* padding-left réduit : pas de case à cocher dans ces lignes, seul
          l'espace du switcher de l'arbre est à compenser. */}
      <div className='doc-tree-header' style={{ gridTemplateColumns: gridColumns, paddingLeft: 24 }}>
        <span className='doc-tree-header-name-col'>
          {sortableHeader('name', 'Nom')}
          <Input
            size='small'
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            suffix={<SearchOutlined />}
            className='doc-tree-header-search'
          />
        </span>
        {!picker && sortableHeader('type', 'Type', true)}
        {!picker && sortableHeader('work', 'Métier', true)}
        {sortableHeader('updated_at', 'Modifié le')}
        {!picker && (
          <span>
            <span className='doc-tree-header-title'>Actions</span>
          </span>
        )}
      </div>
      {renderList()}

      {newWebTemplateModalOpen && (
        <NewWebTemplateModal
          open
          creating={creatingWebTemplate}
          onCreate={handleCreate}
          onCancel={() => setNewWebTemplateModalOpen(false)}
        />
      )}

      <Modal
        open={!!renamer}
        title='Renommer le site web'
        okText='Renommer'
        cancelText={t('cancel')}
        onCancel={() => setRenamer(null)}
        onOk={submitRename}
        okButtonProps={{ disabled: !renamer?.name?.trim() }}
        // Même contournement que le modal de dossier de DocumentsFolderView :
        // `autoFocus` perd la course contre le focus-trap du Modal.
        afterOpenChange={(open) => {
          if (open) renameInputRef.current?.focus();
        }}
        destroyOnClose
      >
        <Input
          ref={renameInputRef}
          placeholder='Nom du site web'
          value={renamer?.name || ''}
          onChange={(e) => setRenamer((prev) => ({ ...prev, name: e.target.value }))}
          onPressEnter={submitRename}
        />
      </Modal>
    </div>
  );
};

const actionIconStyle = { cursor: 'pointer', fontSize: 12, color: 'rgba(0,0,0,0.85)' };

export default WebTemplatesPanel;
